import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, Typography, colors, Button } from '@material-ui/core';
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt';
import { IconAlternate } from 'components/molecules';
import { CardBase } from 'components/organisms';
import { useHistory } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux'
import { setTopicFollowings } from 'redux/actions/FollowActions';
import { setLoading, setCallbackError } from 'redux/actions/LoadingActions';
import { setCommunityCategoryItem } from 'redux/actions/CommunityActions'
import { callApi, callPostApi } from 'utils/networking';

const useStyles = makeStyles(theme => ({
  root: {
    height: '400px',
    width: '100%',
  },
  fontWeight700: {
    fontWeight: 700,
  },
  categoryIconButton: {
    display: 'flex',
    width: '100%',
    flex: 'column',
    justifyContent: 'flex-end',
    position: 'absolute',
    bottom: '30px',
    right: '20px'
  },
}));

/**
 * Component to display the category link card
 *
 * @param {Object} props
 */
const CardCategoryLink = props => {
  const {
    fontIconClass,
    color,
    category,
    categoryID,
    title,
    subtitle,
    href,
    align,
    className,
    iconAlternateProps,
    titleProps,
    subtitleProps,
    ...rest
  } = props;

  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();

  const profileData = useSelector((state) => state.auth.profileData)
  const topicFollowingsData = useSelector((state) => state.follow.topicFollowingsData)

  const [followingStatus, setFollowingStatus] = useState(false);

  useEffect(() => {
    setFollowingStatus(getFollowingStatus(categoryID))
  }, [])

  const getFollowingStatus = (categoryID) => {
    for (let following of topicFollowingsData) {
      if (following.topic_id == categoryID && following.follow_status == 1) {
        return true;
      }
    }
    return false;
  }

  let justifyGrid = 'flex-start';
  if (align === 'center') {
    justifyGrid = 'center';
  } else if (align === 'right') {
    justifyGrid = 'flex-end';
  }

  const updateFollowing = async (categoryID) => {
    if (!profileData || !profileData.id) {
      history.push(`signin`);
      return;
    }

    callPostApi('blog/update_topic_follow_status', { user_id: profileData.id, topic_id: categoryID }, dispatch, (data) => {
      let isInFollowings = false;
      const followingsData = [...topicFollowingsData]
      for (let index=0; index<followingsData.length; index++) {
        let following = followingsData[index];
        if (following.topic_id == categoryID) {
          isInFollowings = true;
          console.log('updated follow', data);
          followingsData[index] = data;
          break;
        }
      }
      if (!isInFollowings) {
        console.log('added follow', data);
        followingsData.push(data);
      }

      const followStatus = (data.follow_status == 1)
      setFollowingStatus(followStatus)
      dispatch(setTopicFollowings(followingsData));
    })
  }

  const moveCommunityPage = (categoryID) => {
    // if (!profileData || !profileData.id) {
    //   history.push(`signin`);
    //   return;
    // }

    //if (getFollowingStatus(categoryID)) {
      dispatch(setCommunityCategoryItem(category));
      history.push(`/community/${categoryID}`);
    //}
  }

  return (
    <CardBase
      className={clsx('card-category-link', classes.root, className)}
      {...rest}
      onClick={e => {
        e.stopPropagation();
        moveCommunityPage(categoryID);
      }}
    >
      <>
      <Grid container spacing={2} className="card-category-link__wrapper">
        <Grid item container justifyContent={justifyGrid} xs={12}>
          <IconAlternate
            fontIconClass={fontIconClass}
            color={color}
            size="medium"
            {...iconAlternateProps}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography
            variant="h6"
            align={align}
            className={classes.fontWeight700}
            {...titleProps}
          >
            {title}
          </Typography>
        </Grid>
        {subtitle && (
          <Grid item xs={12}>
            <Typography
              variant="subtitle1"
              align={align}
              color="textSecondary"
              {...subtitleProps}
            >
              {subtitle}
            </Typography>
          </Grid>
        )}
      </Grid>
      <a
        href={href}
        className={clsx('card-category-link__item', classes.categoryIconButton)}
      >
        <Button className="card-category-link__icon-button"
          onClick={ (e) => {
            e.stopPropagation();
            updateFollowing(categoryID);
          }}>
          {followingStatus ? "Following" : "Follow"}
          <ArrowRightAltIcon
            className="card-category-link__icon"
            style={{ color: color[500] }}
          />
        </Button>
      </a>
      </>
    </CardBase>
  );
};

CardCategoryLink.defaultProps = {
  align: 'left',
  href: '#',
  iconAlternateProps: {},
  titleProps: {},
  subtitleProps: {},
};

CardCategoryLink.propTypes = {
  /**
   * External classes
   */
  className: PropTypes.string,
  /**
   * Promo title to show inside the card
   */
  title: PropTypes.string.isRequired,
  /**
   * Promo subtitle to show inside the card
   */
  subtitle: PropTypes.string,
  /**
   * Promo description to show inside the card
   */
  href: PropTypes.string.isRequired,
  /**
   * Promo font icon class name to show inside the card
   */
  fontIconClass: PropTypes.string,
  /**
   * Promo icon color to show inside the card
   */
  color: PropTypes.oneOf([
    colors.red,
    colors.pink,
    colors.purple,
    colors.deepPurple,
    colors.indigo,
    colors.blue,
    colors.lightBlue,
    colors.cyan,
    colors.teal,
    colors.green,
    colors.lightGreen,
    colors.lime,
    colors.yellow,
    colors.amber,
    colors.orange,
    colors.deepOrange,
    colors.brown,
    colors.grey,
    colors.blueGrey,
  ]).isRequired,
  /**
   * The content alignment
   */
  align: PropTypes.oneOf(['left', 'right', 'center']),
  /**
   * Additional props to pass to the IconAlternate component
   */
  iconAlternateProps: PropTypes.object,
  /**
   * Additional props to pass to the title Typography component
   */
  titleProps: PropTypes.object,
  /**
   * Additional props to pass to the subtitle Typography component
   */
  subtitleProps: PropTypes.object,
  categoryID: PropTypes.string,
  category: PropTypes.object,
};

export default CardCategoryLink;
